import { useAuth } from "@/auth/AuthContext";
import { hasRole } from "@/lib/hasRole";
import { MemberResourceList } from "./MemberResourceList";
import { useEffect, useMemo, useState } from "react";
import { AssignmentReachability } from "@/components/AssignmentReachability";
import { Button } from "@/components/Button";
import { CredentialFields } from "@/components/CredentialFields";
import { DetailList, type DetailRow } from "@/components/DetailList";
import { EntityMultiSelect } from "@/components/EntityMultiSelect";
import { useOutletContext, useParams } from "react-router";
import { HostIdentityModal } from "@/components/HostIdentityModal";
import { ChipList } from "@/components/ChipList";
import { KebabMenu } from "@/components/KebabMenu";
import { SearchField } from "@/components/SearchField";
import { Modal, ModalSubtitle, ModalTitle } from "@/components/Modal";
import { Pagination } from "@/components/Pagination";
import { Select } from "@/components/Select";
import { TagsEditor } from "@/components/TagsEditor";
import { TextInput } from "@/components/TextInput";
import { Toast } from "@/components/Toast";
import { TrafficFlowDiagram } from "@/components/TrafficFlowDiagram";
import {
  ResourceReadiness,
  type ReadinessCheck,
} from "@/components/ResourceReadiness";
import { WizardRail } from "@/components/WizardRail";
import { PAGE_SIZE } from "@/lib/pagination";
import {
  addressLabelFor,
  CONNECTION_METHODS_BY_KIND,
  CONNECTION_METHOD_LABELS,
  CREDENTIAL_INJECTING_CONNECTION_METHODS,
  isManagedKubernetes,
  STATUS_OPTIONS,
  usesOwnIdentity,
  usesPublicCertificate,
} from "./shared";
import { plural } from "@/lib/plural";
import { AksAccessSteps } from "./AksAccessSteps";
import { EksAccessSteps } from "./EksAccessSteps";
import { GkeAccessSteps } from "./GkeAccessSteps";
import { ResourceTable } from "./ResourceTable";
import type { ResourcesLayoutContext } from "./ResourcesLayout";
import { needsAttention } from "./shared";
import { useResourceCrud } from "./useResourceCrud";
import { EmptyState } from "@/components/EmptyState";
import { LoadError } from "@/components/LoadError";

const IDENTITY_CLOUDS: Record<string, string> = {
  AwsIamRole: "AWS",
  AzureManagedIdentity: "Azure",
  GoogleIdentity: "Google Cloud",
};

interface ResourcePageProps {
  type: string;
  typeLabelSentence: string;
  typeLabelPlural: string;
  kindOptions: string[];
  defaultPort: number;
}

export function ResourcePage(props: ResourcePageProps) {
  const { user } = useAuth();
  return hasRole(user?.roles, "Admin") ? (
    <AdminResourcePage {...props} />
  ) : (
    <MemberResourceList key={props.type} {...props} />
  );
}

function AdminResourcePage({
  type,
  typeLabelSentence,
  typeLabelPlural,
  kindOptions,
  defaultPort,
}: ResourcePageProps) {
  const crud = useResourceCrud({
    type,
    typeLabelSentence,
    kindOptions,
    defaultPort,
  });

  const { orgId } = useParams();
  const { setSummary } = useOutletContext<ResourcesLayoutContext>();

  const [hostIdentityResource, setHostIdentityResource] = useState<{
    id: string;
    name: string;
    connectionMethod: string | null;
  } | null>(null);

  const hasKinds = kindOptions.length > 0;

  const resources = crud.resources;

  useEffect(() => {
    if (!resources) {
      return;
    }

    setSummary({
      total: crud.totalCount,
      needsAttention: resources.filter(needsAttention).length,
      typeLabelPlural,
    });

    return () => setSummary(null);
  }, [resources, crud.totalCount, typeLabelPlural, setSummary]);

  const [stepIndex, setStepIndex] = useState(0);

  const steps = useMemo(
    () => [
      {
        id: "general",
        label: "General",
      },
      {
        id: "connectivity",
        label: "Connectivity",
      },
      ...(hasKinds
        ? [
            {
              id: "credentials",
              label: "Credentials",
            },
          ]
        : []),
      {
        id: "confirmation",
        label: "Confirmation",
      },
    ],
    [hasKinds],
  );

  useEffect(() => {
    if (crud.showModal) {
      // oxlint-disable-next-line react/set-state-in-effect
      setStepIndex(0);
    }
  }, [crud.showModal]);

  const activeStep = steps[stepIndex]?.id ?? "general";
  const isLastStep = stepIndex === steps.length - 1;

  const connectionMethod = CONNECTION_METHODS_BY_KIND[crud.formKind]?.[0];

  const injectsCredentials =
    connectionMethod !== undefined &&
    CREDENTIAL_INJECTING_CONNECTION_METHODS.includes(connectionMethod);

  const hasGateway = crud.formGatewayIds.length > 0;
  const hasPath = hasGateway || crud.formConnectorIds.length > 0;

  const gatewayNames = crud.formGatewayIds
    .map((id) => crud.formGatewaySelections[id]?.name)
    .filter((name): name is string => Boolean(name));
  const connectorNames = crud.formConnectorIds
    .map((id) => crud.formConnectorSelections[id]?.name)
    .filter((name): name is string => Boolean(name));
  const hasMultipleFlow = gatewayNames.length > 1 || connectorNames.length > 1;

  const addressLabel = addressLabelFor(type, crud.formKind);

  const hostedGatewaySignsIn =
    usesOwnIdentity(crud.formCredentialType) &&
    crud.formConnectorIds.length === 0 &&
    crud.formGatewayIds.some(
      (id) => crud.formGatewaySelections[id]?.source === "Hosted",
    );

  const hostedGatewayIdentityError = `Hosted gateways can't use their own ${IDENTITY_CLOUDS[crud.formCredentialType]} identity`;

  const keptCredential =
    crud.editingCredential?.credentialType === crud.formCredentialType
      ? crud.editingCredential
      : null;

  const credentialHasSecrets =
    crud.formCredentialType.length > 0 &&
    (crud.formCredentialType === "BearerToken" ||
      crud.formCredentialType === "GoogleServiceAccount" ||
      crud.formCredentialUsername.trim().length > 0) &&
    (crud.formCredentialType === "UsernameKeyPair"
      ? crud.formCredentialPrivateKey.trim().length > 0 ||
        Boolean(keptCredential?.hasPrivateKey)
      : crud.formCredentialPassword.trim().length > 0 ||
        Boolean(keptCredential?.hasPassword)) &&
    (crud.formCredentialType === "DomainUsernamePassword" ||
    crud.formCredentialType === "AzureServicePrincipal"
      ? crud.formCredentialDomain.trim().length > 0
      : true);

  const usesAwsSignIn =
    crud.formKind === "EKS" &&
    (crud.formCredentialType === "AwsIamRole" ||
      crud.formCredentialType === "AwsAccessKey");

  const credentialIsComplete =
    (usesOwnIdentity(crud.formCredentialType)
      ? !hostedGatewaySignsIn
      : credentialHasSecrets) &&
    (!usesAwsSignIn || crud.formClusterName.trim().length > 0);

  const takesCertificateAuthority =
    isManagedKubernetes(crud.formKind) &&
    !usesPublicCertificate(crud.formKind, crud.formAddress);

  const stepIsComplete: Record<string, boolean> = {
    general:
      crud.formName.trim().length > 0 &&
      (!hasKinds || crud.formKind.length > 0),
    connectivity:
      crud.formAddress.trim().length > 0 &&
      crud.formPort !== "" &&
      (!takesCertificateAuthority ||
        crud.editingResource !== null ||
        crud.formCertificateAuthority.trim().length > 0),
    credentials: credentialIsComplete,
    confirmation: true,
  };

  const canAdvance = stepIsComplete[activeStep] ?? true;

  const firstIncompleteIndex = steps.findIndex(
    (step) => !(stepIsComplete[step.id] ?? true),
  );

  const furthestReachable =
    firstIncompleteIndex === -1 ? steps.length - 1 : firstIncompleteIndex;

  const canNavigateTo = (index: number) =>
    index < stepIndex || index <= furthestReachable;

  const nameAndKind = [crud.formName.trim(), crud.formKind]
    .filter((part) => part.length > 0)
    .join(" · ");

  const readinessChecks: ReadinessCheck[] = [
    {
      label: hasKinds ? "Name and kind" : "Name",
      detail: nameAndKind.length > 0 ? nameAndKind : undefined,
      state: stepIsComplete.general ? "met" : "unmet",
    },
    {
      label: `${addressLabel} and port`,
      detail:
        crud.formAddress.trim().length > 0 && crud.formPort !== ""
          ? `${crud.formAddress.trim()}:${crud.formPort}`
          : undefined,
      state: stepIsComplete.connectivity ? "met" : "unmet",
    },
    {
      label: "Connectivity",
      detail: hasGateway
        ? [...gatewayNames, ...connectorNames].join(" · ")
        : "Assign at least one gateway",
      state: hasGateway ? "met" : "unmet",
    },
    ...(injectsCredentials
      ? [
          {
            label: "Credentials",
            detail:
              crud.formCredentialType.length === 0
                ? `${CONNECTION_METHOD_LABELS[connectionMethod!] ?? connectionMethod} injects a credential, so one is required`
                : hostedGatewaySignsIn
                  ? hostedGatewayIdentityError
                  : usesAwsSignIn && crud.formClusterName.trim().length === 0
                    ? "Cluster name is required"
                    : undefined,
            state: credentialIsComplete ? ("met" as const) : ("unmet" as const),
          },
        ]
      : []),
  ];

  const summaryRows: DetailRow[] = [
    { label: "Name", value: crud.formName.trim() || "Not set" },
    ...(hasKinds
      ? [
          {
            label: "Kind",
            value: [
              crud.formKind,
              connectionMethod
                ? (CONNECTION_METHOD_LABELS[connectionMethod] ??
                  connectionMethod)
                : null,
            ]
              .filter(Boolean)
              .join(" · "),
          },
        ]
      : []),
    {
      label: addressLabel,
      value:
        crud.formAddress.trim() && crud.formPort !== ""
          ? `${crud.formAddress.trim()}:${crud.formPort}`
          : "Not set",
    },
    ...(usesAwsSignIn
      ? [
          {
            label: "Cluster name",
            value: crud.formClusterName.trim() || "Not set",
          },
        ]
      : []),
    ...(takesCertificateAuthority
      ? [
          {
            label: "Certificate authority",
            value: crud.formCertificateAuthority.trim()
              ? "Entered"
              : crud.editingResource
                ? "Unchanged"
                : "Not set",
          },
        ]
      : []),
    {
      label: "Route",
      value: hasPath
        ? [...gatewayNames, ...connectorNames].join(" · ")
        : "Nothing assigned",
    },
    ...(injectsCredentials
      ? [
          {
            label: "Credential",
            value: crud.formCredentialUsername.trim()
              ? crud.formCredentialUsername.trim()
              : "None",
          },
        ]
      : []),
    {
      label: "Tags",
      value:
        crud.formTags.filter((tag) => tag.key.trim()).length === 0 ? (
          "None"
        ) : (
          <ChipList
            items={crud.formTags
              .filter((tag) => tag.key.trim())
              .map((tag) => `${tag.key}=${tag.value}`)}
            shown={4}
            variant="mono"
          />
        ),
    },
  ];

  const gatewayReachability = (crud.editingResource?.gateways ?? [])
    .filter((g) => crud.formGatewayIds.includes(g.gatewayId))
    .map((g) => ({
      id: g.gatewayId,
      name: g.gatewayName,
      reachable: g.reachable,
      lastCheckedAt: g.lastCheckedAt,
      online: g.online,
    }));
  const connectorReachability = (crud.editingResource?.connectors ?? [])
    .filter((c) => crud.formConnectorIds.includes(c.connectorId))
    .map((c) => ({
      id: c.connectorId,
      name: c.connectorName,
      reachable: c.reachable,
      lastCheckedAt: c.lastCheckedAt,
      online: c.online,
    }));

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <SearchField
          value={crud.nameFilter}
          onChange={crud.setNameFilter}
          label={`Search ${typeLabelPlural} by name`}
          placeholder={`Search ${typeLabelSentence} name`}
        />
        <Select
          aria-label="Filter by gateway"
          value={crud.gatewayFilter}
          onChange={(e) => crud.setGatewayFilter(e.target.value)}
        >
          <option value="">All gateways</option>
          {crud.gatewayFilterOptions.map((gateway) => (
            <option key={gateway.id} value={gateway.id}>
              {gateway.name}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filter by connector"
          value={crud.connectorFilter}
          onChange={(e) => crud.setConnectorFilter(e.target.value)}
        >
          <option value="">All connectors</option>
          {crud.connectorFilterOptions.map((connector) => (
            <option key={connector.id} value={connector.id}>
              {connector.name}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filter by status"
          value={crud.statusFilter}
          onChange={(e) => crud.setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </Select>
        {hasKinds && (
          <Select
            aria-label="Filter by kind"
            value={crud.kindFilter}
            onChange={(e) => crud.setKindFilter(e.target.value)}
          >
            <option value="">All kinds</option>
            {kindOptions.map((kind) => (
              <option key={kind} value={kind}>
                {kind}
              </option>
            ))}
          </Select>
        )}
      </div>

      {crud.hasFilters && resources !== null && (
        <div className="mb-4 flex items-center gap-3 text-sm text-foreground/70">
          <span>Filtered to {plural(crud.totalCount, typeLabelSentence)}</span>
          <button
            type="button"
            onClick={crud.clearFilters}
            className="rounded text-accent hover:underline focus:outline-none focus:ring-2 focus:ring-accent/20"
          >
            Clear filters
          </button>
        </div>
      )}

      {crud.showModal && (
        <Modal
          onClose={() => crud.setShowModal(false)}
          maxWidth="max-w-4xl"
          scrollable={false}
        >
          <div className="mb-4 shrink-0">
            <ModalTitle>
              {crud.editingResource
                ? `Edit ${typeLabelSentence}`
                : `Add a ${typeLabelSentence}`}
            </ModalTitle>
            <ModalSubtitle className="mt-1">
              Step {stepIndex + 1} of {steps.length}
            </ModalSubtitle>
          </div>
          <form
            onSubmit={(event) => {
              if (!isLastStep) {
                event.preventDefault();

                if (canAdvance) {
                  setStepIndex(stepIndex + 1);
                }

                return;
              }

              crud.handleSubmit(event);
            }}
            className="flex min-h-0 flex-1 flex-col"
          >
            <WizardRail
              steps={steps}
              activeIndex={stepIndex}
              isNavigable={canNavigateTo}
              onStepSelect={setStepIndex}
            />
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
              <>
                {activeStep === "general" && (
                  <>
                    <div>
                      <label
                        htmlFor="resourceName"
                        className="mb-1.5 block text-sm text-foreground"
                      >
                        Name
                      </label>
                      <TextInput
                        id="resourceName"
                        value={crud.formName}
                        onChange={(e) => crud.setFormName(e.target.value)}
                        required
                        autoFocus
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="resourceDescription"
                        className="mb-1.5 block text-sm text-foreground"
                      >
                        Description{" "}
                        <span className="font-normal text-foreground/70">
                          (optional)
                        </span>
                      </label>
                      <textarea
                        id="resourceDescription"
                        value={crud.formDescription}
                        onChange={(e) =>
                          crud.setFormDescription(e.target.value)
                        }
                        rows={2}
                        className="w-full rounded-lg border border-muted bg-surface px-3 py-2 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                      />
                    </div>
                    {hasKinds && (
                      <div>
                        <label
                          htmlFor="resourceKind"
                          className="mb-1.5 block text-sm text-foreground"
                        >
                          Kind
                        </label>
                        <Select
                          id="resourceKind"
                          value={crud.formKind}
                          onChange={(e) =>
                            crud.handleFormKindChange(e.target.value)
                          }
                          required
                          className="w-full"
                        >
                          <option value="" disabled>
                            Select a kind...
                          </option>
                          {kindOptions.map((kind) => (
                            <option key={kind} value={kind}>
                              {kind}
                            </option>
                          ))}
                        </Select>
                      </div>
                    )}
                    <TagsEditor
                      tags={crud.formTags}
                      onChange={crud.setFormTags}
                    />
                  </>
                )}
                {activeStep === "connectivity" && (
                  <>
                    <div className="flex items-end gap-3">
                      <div className="min-w-0 basis-3/4">
                        <label
                          htmlFor="resourceAddress"
                          className="mb-1.5 block text-sm text-foreground"
                        >
                          {addressLabel}
                        </label>
                        <TextInput
                          id="resourceAddress"
                          value={crud.formAddress}
                          onChange={(e) => crud.setFormAddress(e.target.value)}
                          required
                          className="w-full"
                        />
                      </div>
                      <div className="min-w-0 basis-1/4">
                        <label
                          htmlFor="resourcePort"
                          className="mb-1.5 block text-sm text-foreground"
                        >
                          Port
                        </label>
                        <TextInput
                          type="number"
                          id="resourcePort"
                          value={crud.formPort}
                          onChange={(e) =>
                            crud.setFormPort(
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value),
                            )
                          }
                          required
                          min={1}
                          max={65535}
                          className="w-full"
                        />
                      </div>
                    </div>
                    {takesCertificateAuthority && (
                      <div>
                        <label
                          htmlFor="resourceCertificateAuthority"
                          className="mb-1.5 block text-sm text-foreground"
                        >
                          Certificate authority
                        </label>
                        <textarea
                          id="resourceCertificateAuthority"
                          value={crud.formCertificateAuthority}
                          onChange={(e) =>
                            crud.setFormCertificateAuthority(e.target.value)
                          }
                          placeholder={
                            crud.editingResource
                              ? "················"
                              : "LS0tLS1CRUdJTi... or a PEM certificate"
                          }
                          required={!crud.editingResource}
                          rows={3}
                          className="w-full rounded-lg border border-muted bg-surface px-3 py-2 font-mono text-xs break-all text-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                        />
                      </div>
                    )}

                    <EntityMultiSelect
                      label="Gateways"
                      searchLabel="Search gateways by name"
                      searchPlaceholder="Search gateway name"
                      selectedIds={crud.formGatewayIds}
                      options={crud.formGatewayResults}
                      emptyMessage="No gateways match your search."
                      nameFilter={crud.formGatewayNameFilter}
                      onNameFilterChange={crud.setFormGatewayNameFilter}
                      page={crud.formGatewayPage}
                      totalPages={Math.ceil(
                        crud.formGatewayTotalCount / PAGE_SIZE,
                      )}
                      onPageChange={crud.setFormGatewayPage}
                      onToggle={crud.toggleFormGateway}
                      selections={crud.formGatewaySelections}
                      renderLabel={(g) =>
                        `${g.name} (${g.source === "Hosted" ? "Hosted" : "Self-hosted"})`
                      }
                    />
                    <AssignmentReachability
                      label="Gateway reachability"
                      assignments={gatewayReachability}
                      routingConnectors={connectorReachability}
                    />
                    {crud.formConnectorsExist && (
                      <EntityMultiSelect
                        label="Connectors"
                        searchLabel="Search connectors by name"
                        searchPlaceholder="Search connector name"
                        requirement="optional"
                        selectedIds={crud.formConnectorIds}
                        options={crud.formConnectorResults}
                        emptyMessage="No connectors match your search."
                        nameFilter={crud.formConnectorNameFilter}
                        onNameFilterChange={crud.setFormConnectorNameFilter}
                        page={crud.formConnectorPage}
                        totalPages={Math.ceil(
                          crud.formConnectorTotalCount / PAGE_SIZE,
                        )}
                        onPageChange={crud.setFormConnectorPage}
                        onToggle={crud.toggleFormConnector}
                        selections={crud.formConnectorSelections}
                      />
                    )}
                    <AssignmentReachability
                      label="Connector reachability"
                      assignments={connectorReachability}
                    />
                    {crud.formGatewayIds.length > 0 && (
                      <TrafficFlowDiagram
                        nodes={[
                          { role: "From", label: "Client" },
                          {
                            role: "Via gateway",
                            label: gatewayNames.join(", "),
                          },
                          ...(connectorNames.length > 0
                            ? [
                                {
                                  role: "Via connector",
                                  label: connectorNames.join(", "),
                                },
                              ]
                            : []),
                          {
                            role: "To",
                            label: crud.formName.trim() || typeLabelSentence,
                          },
                        ]}
                        helperText={
                          hasMultipleFlow
                            ? "When multiple gateways or connectors are assigned, Tendra automatically routes each session through the fastest reachable one."
                            : undefined
                        }
                      />
                    )}
                  </>
                )}
                {activeStep === "credentials" && (
                  <>
                    {hasKinds && (
                      <CredentialFields
                        credentialTypeOptions={crud.credentialTypeOptions}
                        credentialType={crud.formCredentialType}
                        onCredentialTypeChange={crud.setFormCredentialType}
                        domain={crud.formCredentialDomain}
                        onDomainChange={crud.setFormCredentialDomain}
                        username={crud.formCredentialUsername}
                        onUsernameChange={crud.setFormCredentialUsername}
                        password={crud.formCredentialPassword}
                        onPasswordChange={crud.setFormCredentialPassword}
                        privateKey={crud.formCredentialPrivateKey}
                        onPrivateKeyChange={crud.setFormCredentialPrivateKey}
                        passphrase={crud.formCredentialPassphrase}
                        onPassphraseChange={crud.setFormCredentialPassphrase}
                        editingCredential={keptCredential}
                      />
                    )}
                    {usesAwsSignIn && !hostedGatewaySignsIn && (
                      <div>
                        <label
                          htmlFor="resourceClusterName"
                          className="mb-1.5 block text-sm text-foreground"
                        >
                          Cluster name
                        </label>
                        <TextInput
                          id="resourceClusterName"
                          value={crud.formClusterName}
                          onChange={(e) =>
                            crud.setFormClusterName(e.target.value)
                          }
                          required
                          className="w-full"
                        />
                      </div>
                    )}
                    {hostedGatewaySignsIn ? (
                      <p className="text-sm text-danger">
                        {hostedGatewayIdentityError}. Use a self-hosted gateway
                        or connector instead.
                      </p>
                    ) : crud.formKind === "EKS" ? (
                      <EksAccessSteps
                        credentialType={crud.formCredentialType}
                        address={crud.formAddress}
                        clusterName={crud.formClusterName}
                      />
                    ) : crud.formKind === "AKS" ? (
                      <AksAccessSteps
                        credentialType={crud.formCredentialType}
                        clientId={crud.formCredentialUsername}
                      />
                    ) : (
                      crud.formKind === "GKE" && (
                        <GkeAccessSteps
                          credentialType={crud.formCredentialType}
                          serviceAccountKey={crud.formCredentialPassword}
                          keptClientEmail={keptCredential?.username ?? null}
                        />
                      )
                    )}
                  </>
                )}
                {activeStep === "confirmation" && (
                  <>
                    <DetailList rows={summaryRows} />
                    <ResourceReadiness checks={readinessChecks} />
                  </>
                )}
              </>
            </div>
            <div className="mt-4 flex shrink-0 justify-end gap-3">
              {stepIndex === 0 ? (
                <Button onClick={() => crud.setShowModal(false)}>Cancel</Button>
              ) : (
                <Button onClick={() => setStepIndex(stepIndex - 1)}>
                  Back
                </Button>
              )}
              {isLastStep ? (
                <Button
                  key="wizard-submit"
                  type="submit"
                  variant="primary"
                  disabled={crud.submitting}
                >
                  {crud.submitting
                    ? "Saving..."
                    : crud.editingResource
                      ? "Save"
                      : `Add ${typeLabelSentence}`}
                </Button>
              ) : (
                <Button
                  key="wizard-next"
                  variant="primary"
                  onClick={() => setStepIndex(stepIndex + 1)}
                  disabled={!canAdvance}
                >
                  Next
                </Button>
              )}
            </div>
          </form>
        </Modal>
      )}

      {hostIdentityResource && (
        <HostIdentityModal
          orgId={orgId ?? ""}
          resourceId={hostIdentityResource.id}
          resourceName={hostIdentityResource.name}
          connectionMethod={hostIdentityResource.connectionMethod}
          onDone={(message) => {
            setHostIdentityResource(null);
            crud.showToast(message, "success");
          }}
          onClose={() => setHostIdentityResource(null)}
        />
      )}

      {crud.error && !resources && (
        <LoadError
          what={typeLabelPlural}
          onRetry={crud.retry}
          className="mb-4"
        />
      )}

      {resources !== null && crud.totalCount === 0 ? (
        <EmptyState
          title={
            crud.hasFilters
              ? `No ${typeLabelPlural} match these filters`
              : `No ${typeLabelPlural} yet`
          }
          description={
            crud.hasFilters
              ? "Try a different name, status or kind."
              : `Add a ${typeLabelSentence} to manage access through Tendra.`
          }
        >
          {crud.hasFilters && (
            <Button onClick={crud.clearFilters}>Clear filters</Button>
          )}
        </EmptyState>
      ) : (
        <ResourceTable
          resources={resources}
          hasKinds={hasKinds}
          renderActions={(resource) => (
            <KebabMenu
              label={`More actions for ${resource.name}`}
              items={[
                {
                  label: "Edit",
                  onClick: () => crud.openEditModal(resource),
                },
                ...((type === "VirtualMachine" ||
                  type === "KubernetesCluster") &&
                !usesPublicCertificate(resource.kind ?? "", resource.address)
                  ? [
                      {
                        label: "Host identity",
                        onClick: () =>
                          setHostIdentityResource({
                            id: resource.id,
                            name: resource.name,
                            connectionMethod: resource.connectionMethod,
                          }),
                      },
                    ]
                  : []),
                {
                  label: "Delete",
                  danger: true,
                  onClick: () => crud.handleDelete(resource),
                },
              ]}
            />
          )}
        />
      )}

      <Pagination
        page={crud.page}
        totalPages={Math.ceil(crud.totalCount / PAGE_SIZE)}
        onPageChange={crud.setPage}
      />

      <Toast toast={crud.toast} />
      {crud.confirmDialog}
    </div>
  );
}
